import { useEffect, useRef, useState, useMemo } from 'react';
import { AggregatedPositionDTO, PartialQuote, PositionDTO, Order } from '@tradingblock/api';

// Create a singleton worker instance
let globalWorker: Worker | null = null;
let isWorkerInitialized = false;
let initializationPromise: Promise<void> | null = null;

export interface PositionData {
  dayPL: number;
  totalPL: number;
  totalCost: number;
  currentValue: number;
  lastPrice: number;
  closePrice: number;
  openQty: number;
  multiplier: number;
  orderId: string;
}

export interface OrderGroupData {
  dayPL: number;
  totalPL: number;
  totalCost: number;
  currentValue: number;
  positions: string[];
}

export interface StrategyGroupData {
  strategyId: string;
  dayPL: number;
  totalPL: number;
  totalCost: number;
  currentValue: number;
  positions: string[];
  orders: string[];
  symbols: string[];
}

export interface PLResult {
  [key: string]: {
    dayPL: number;
    totalPL: number;
    tradingPL?: number; // Trading P/L from orders (for closed positions)
    totalCost: number;
    currentValue: number;
    tradedSymbols?: string[]; // Symbols that were traded for this underlying (for closed positions)
    positions: {
      [key: string]: PositionData;
    };
    orderGroups: {
      [key: string]: OrderGroupData;
    };
    strategyGroups: {
      [key: string]: StrategyGroupData;
    };
  };
}

// Initialize worker function
const initializeWorker = async () => {
  if (isWorkerInitialized) return;
  if (initializationPromise) return initializationPromise;

  initializationPromise = new Promise((resolve, reject) => {
    try {
      globalWorker = new Worker(`${process.env.PUBLIC_URL}/plWorker.js`);

      // Set up message handler
      globalWorker.onmessage = event => {
        // Store the last known values
        lastKnownValues = event.data.plMap;
      };

      globalWorker.onmessageerror = (event: MessageEvent) => {
        console.error('Worker message error:', event);
        reject(event);
      };

      isWorkerInitialized = true;
      resolve();
    } catch (error) {
      console.error('Error initializing Worker:', error);
      reject(error);
    }
  });

  return initializationPromise;
};

// Store last known values
let lastKnownValues: PLResult = {};

// Pre-initialize the worker
initializeWorker().catch(console.error);

// Add batching for worker messages
let batchTimeout: NodeJS.Timeout | null = null;

export function usePLWorker(
  positions: (PositionDTO | AggregatedPositionDTO)[],
  quotes: PartialQuote[],
  accountId?: string,
  subAccountFilterId?: number,
  marketSchedule?: any,
  orders?: Order[]
): { plMap: PLResult; isLoading: boolean } {
  const [plMap, setPlMap] = useState<PLResult>(lastKnownValues);
  const [isInitialLoading, setIsInitialLoading] = useState(!isWorkerInitialized);
  const [hasData, setHasData] = useState(false);
  const workerRef = useRef<Worker | null>(null);
  const batchedPositions = useRef<(PositionDTO | AggregatedPositionDTO)[]>([]);
  const hasInitialized = useRef(false);

  // Memoize positions and quotes to prevent unnecessary worker updates
  const memoizedPositions = useMemo(() => positions, [
    positions
      .map(p => `${p.Symbol}-${p.OpenQuantity}-${p.OpenPrice}-${p.UseCalculatedPnL}-${p.DailyPnL}-${p.OverallPnl}`)
      .join(','),
  ]);

  // Only recalculate when quotes are added/removed, not on price changes
  const memoizedQuotes = useMemo(() => quotes, [
    quotes
      .map(q => `${q.Symbol}-${q.AskPrice}-${q.BidPrice}-${q.IsSettlementSet}-${q.ClosePrice}-${q.ClosePriceTMinus2}`)
      .join(','),
  ]);

  // Memoize orders to prevent unnecessary worker updates
  const memoizedOrders = useMemo(() => orders || [], [
    orders
      ? orders.map(o => `${o.OrderId}-${o.Date}-${o.OrderStatus}-${(o.Legs || []).length}`).join(',')
      : '',
  ]);

  // Reset plMap when account changes
  useEffect(() => {
    setPlMap({});
    setHasData(false);
    lastKnownValues = {};
    setIsInitialLoading(true);
    hasInitialized.current = false;

    // Clear any pending batch timeout
    if (batchTimeout) {
      clearTimeout(batchTimeout);
      batchTimeout = null;
    }
  }, [accountId, subAccountFilterId]);

  useEffect(() => {
    // Initialize worker if not already initialized
    if (!isWorkerInitialized) {
      initializeWorker().then(() => {
        setIsInitialLoading(false);
        hasInitialized.current = true;
      });
    } else if (!hasInitialized.current) {
      // Worker is already initialized, mark as ready
      setIsInitialLoading(false);
      hasInitialized.current = true;
    }

    // Use global worker
    workerRef.current = globalWorker;

    // Send data to worker
    if (workerRef.current) {
      // Process through worker even if no positions (may have orders for trading P/L)
      batchedPositions.current = memoizedPositions;

      if (batchTimeout) {
        clearTimeout(batchTimeout);
      }

      batchTimeout = setTimeout(() => {
        // Send batched update with subAccountFilterId, marketSchedule, and orders
        workerRef.current &&
          workerRef.current.postMessage({
            positions: batchedPositions.current,
            quotes: memoizedQuotes,
            orders: memoizedOrders,
            subAccountFilterId: subAccountFilterId,
            marketSchedule: marketSchedule,
            accountId: accountId, // Include accountId to ensure fresh calculation
          });
        batchedPositions.current = [];
      }, 100);
    }

    // Enhanced cleanup function
    return () => {
      if (workerRef.current) {
        // Send cleanup message to worker
        workerRef.current.postMessage({ type: 'cleanup' });
        workerRef.current = null;
      }
      if (batchTimeout) {
        clearTimeout(batchTimeout);
      }
    };
  }, [memoizedPositions, memoizedQuotes, memoizedOrders, subAccountFilterId, marketSchedule]);

  // Update local state when lastKnownValues changes
  // Use an interval to check for updates since lastKnownValues is a global variable
  useEffect(() => {
    const checkForUpdates = () => {
      if (Object.keys(lastKnownValues).length > 0) {
        // Only update if values have actually changed
        setPlMap((prev: PLResult) => {
          if (JSON.stringify(prev) === JSON.stringify(lastKnownValues)) {
            return prev;
          }
          setHasData(true);
          // Only clear initial loading on first data load
          if (isInitialLoading) {
            setIsInitialLoading(false);
            hasInitialized.current = true;
          }
          return lastKnownValues;
        });
      }
    };

    // Check immediately
    checkForUpdates();

    // Set up interval to check for updates every 100ms
    const interval = setInterval(checkForUpdates, 100);

    return () => clearInterval(interval);
  }, [isInitialLoading]);

  // return the last known values if the worker is not initialized
  // and also return the current loading state
  return { plMap, isLoading: isInitialLoading };
}
